import React, { useCallback } from 'react';
import { Range } from 'react-range';
import c from 'classnames';

import GenericFormElement from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/GenericFormElement';

import type { IRenderThumbParams, IRenderTrackParams } from 'react-range/lib/types';

import './style.scss';
import { useField } from 'formik';

type FormRangeProps = {
  max: number
  min: number
  step: number
  defaultValue: number,
  prefix?: string
  suffix?: string
  wrapperClassName?: string
  fieldName: string
  title?: string
  required?: boolean
  description?: string
  placeholder?: string
  descriptionAboveInput?: boolean
  [key: string]: unknown
};

const FormRange: React.FC<FormRangeProps> = ({
  min,
  max,
  step,
  defaultValue,
  prefix = '',
  suffix = '',
  title,
  required,
  description,
  fieldName,
  wrapperClassName,
  descriptionAboveInput,
  children,
  ...props
}) => {
  const [{ value = defaultValue }, { error, touched }, { setValue, setError }] = useField<number>(fieldName);

  const errorId = `${fieldName}--error`;
  const descriptionId = `${fieldName}--description`;

  const renderTrack = useCallback(({ props: { ref, style, ...trackProps }, children: trackChildren }: IRenderTrackParams) => (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      style={{
        // @ts-expect-error
        '--track-value': value,
        '--track-min': min,
        '--track-max': max,
      }}
      className="FormRange__track"
      {...trackProps}
    >
      <div
        ref={ref}
        className="FormRange__track__inner"
      >
        {trackChildren}
      </div>
    </div>
  ), [value, min, max]);

  const renderThumb = useCallback(({ props: { key, ...thumbProps }, isDragged }: IRenderThumbParams) => (
    <div
      key={key}
      className={c('FormRange__thumb', {
        'FormRange__thumb--active': isDragged,
      })}
      {...thumbProps}
    >
      {value && (
        <div className="FormRange__thumb__tooltip">
          {prefix}{value}{suffix}
        </div>
      )}
      <div className="FormRange__thumb__bar" />
      <div className="FormRange__thumb__bar" />
      <div className="FormRange__thumb__bar" />
    </div>
  ), [value, prefix, suffix]);

  return (
    <GenericFormElement
      className={c('FormRange', wrapperClassName)}
      touched={touched}
      {...{
        title,
        error,
        required,
        fieldName,
        description,
        descriptionAboveInput,
        descriptionId,
        errorId,
      }}
    >
      <div className="FormRange__description">
        <div className="FormRange__description__min">{prefix}{min}{suffix}</div>
        <div className="FormRange__description__max">{prefix}{max}{suffix}</div>
      </div>
      <Range
        values={[value]}
        step={step}
        min={min}
        max={max}
        onChange={([newValue]: number[]) => {
          if (newValue) {
            setValue(newValue)
              .catch(() => setError(`Invalid input for ${fieldName}`));
          }
        }}
        renderTrack={renderTrack}
        renderThumb={renderThumb}
        {...props}
      />
    </GenericFormElement>
  );
};

export default FormRange;
